/**
 * Helpers for reading the launcher config injected as window.__OPTIONS__.
 */

/**
 * The Home button is shown only when the launcher provides all three: the
 * ui.home_button flag, a non-empty actions.return, and a non-empty
 * games_page_url. Shared by ButtonHome (its render gate) and ButtonReplay
 * (which slides into Home's toolbar slot when Home is hidden), so the two
 * can't drift out of sync.
 * @returns {boolean}
 */
export const isHomeButtonVisible = () =>
  window.__OPTIONS__?.ui?.home_button === true &&
  !!window.__OPTIONS__?.actions?.return &&
  !!window.__OPTIONS__?.games_page_url;

/**
 * True when this page was launched as a standalone replay link (a shared
 * "Copy link" URL opened directly, not the in-game History→Play hot-swap
 * path — see docs/replay-history-spec.md §0 Standalone Replay Link plan).
 * The launcher sets this in window.__OPTIONS__ itself — confirmed live
 * (2026-08-10) against a real ThreeOrbsOfZeus replay link: `server_id`
 * comes back exactly `"replay"`, with `api`/`history_url`/`games_page_url`
 * all empty/null (nothing live to fall back to in this mode).
 * @returns {boolean}
 */
export const isStandaloneReplayMode = () =>
  window.__OPTIONS__?.server_id === "replay";

/**
 * The replay API base URL for a standalone replay link, parsed from the
 * page's own query string (NOT window.__OPTIONS__ — confirmed live
 * (2026-08-10) that `replayUrl` only ever appears in the URL). Same param
 * name and shape RGS.js's _extractReplayUrl() already parses out of a
 * history row's `replay_link` for the in-game flow — `round_id`/
 * `play_token`/`replay_player_id` are NOT needed here: `replayUrl` alone is
 * a complete, pre-signed replay API path.
 * @returns {string|null}
 */
export const getStandaloneReplayUrl = () => {
  try {
    return new URLSearchParams(window.location.search).get("replayUrl") || null;
  } catch {
    return null;
  }
};
